/**
 * Google Maps Script Loader (V2)
 *
 * Loads Google Maps JavaScript API with Places library.
 * Uses singleton pattern to prevent duplicate script injection.
 *
 * @returns {Promise<void>} Resolves when Google Maps is loaded
 */
let loadPromise = null;
let isLoaded = false;

const loadGoogleMapsV2 = () => {
	// If already loaded, resolve immediately
	if (isLoaded && window.google && window.google.maps) {
		return Promise.resolve();
	}

	// If already loading, return existing promise
	if (loadPromise) {
		return loadPromise;
	}

	// Check if script already exists in DOM
	const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
	if (existingScript && window.google && window.google.maps) {
		isLoaded = true;
		return Promise.resolve();
	}

	// Get API key from environment
	const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

	if (!apiKey) {
		const error = new Error(
			'REACT_APP_GOOGLE_MAPS_KEY is not defined in environment variables. Please add it to your .env file.'
		);
		console.error('Google Maps API Key Error:', error.message);
		console.warn(
			'To fix this: Add REACT_APP_GOOGLE_MAPS_KEY=your_api_key to your .env.local or .env file'
		);
		return Promise.reject(error);
	}

	// Create new promise for loading
	loadPromise = new Promise((resolve, reject) => {
		// Create unique callback name
		const callbackName = `initGoogleMapsV2_${Date.now()}`;

		// Set up global callback
		window[callbackName] = () => {
			isLoaded = true;
			delete window[callbackName];
			resolve();
		};

		// Create script element
		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${callbackName}`;
		script.async = true;
		script.defer = true;

		// Handle script load errors
		script.onerror = () => {
			delete window[callbackName];
			const error = new Error('Failed to load Google Maps script');
			console.error('Google Maps Script Load Error:', error);
			loadPromise = null;
			reject(error);
		};

		// Add script to document
		document.head.appendChild(script);

		// Timeout after 30 seconds
		setTimeout(() => {
			if (!isLoaded) {
				delete window[callbackName];
				const error = new Error('Google Maps script load timeout');
				console.error('Google Maps Script Load Timeout:', error);
				loadPromise = null;
				reject(error);
			}
		}, 30000);
	});

	return loadPromise;
};

export default loadGoogleMapsV2;
